/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {FC, useEffect, useState} from 'react';
import {
  Grid,
  IconButton,
  makeStyles
} from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import {
  getMonth,
  getYear
} from 'date-fns';
import {translate} from '@ion/core/src/locale/locale';
import Dropdown from '../../components/menus/Dropdown';
import {dlsColor} from '../../theme/theme';

// defines the styles used by the component
const useStyles = makeStyles(theme => ({
  icon : {
    '&:hover' : {
      background : 'none'
    }
  },
  header : {
    fontSize      : '16px',
    lineHeight    : '24px',
    letterSpacing : '1px',
    fontFamily    : 'roboto',
    fontWeight    : 700,
    fontStyle     : 'normal',
    color         : dlsColor('grey1'),
    textTransform : 'uppercase',
    display       : 'flex'
  }
}));

// defines the prop definitions for the component
export interface HeaderProps {
  date?: Date;
  onMonthNavigate: (action: number) => void;
  onYearNavigate: (action: number) => void;
  onNext: () => void;
  onPrevious: () => void;
}

/**
 * The function returns the array of month values. The result will be like
 * ['January', ' February', ... , 'December'].
 *
 * @returns array of month values
 */
export const getMonths = () => {
  const months = translate('common:editors.dateTime.months', {returnObjects: true});
  return months?.reduce((result, month) => {
    result.push(month.value);
    return result;
  }, []);
};

/**
 * @Header
 *
 * Defines the Header component. It provides user with a drop down to select months and year respectively
 * along with left and right arrow buttons to navigate between months.
 */
export const Header: FC<HeaderProps> = ({
  date,
  onNext,
  onPrevious,
  onMonthNavigate,
  onYearNavigate
}: HeaderProps) => {
  const classes           = useStyles();
  const MONTH             = getMonths();
  const [month, setMonth] = useState(MONTH[getMonth(date)]);
  const [year, setYear]   = useState(getYear(date).toString());

  useEffect(() => {
    setMonth(MONTH[getMonth(date)]);
    setYear(getYear(date).toString());
  }, [date]);

  function changeMonth({target}) {
    const {value} = target;
    onMonthNavigate(MONTH.indexOf(value) - MONTH.indexOf(month));
    setMonth(value);
  }

  function changeYear({target}) {
    const {value} = target;
    onYearNavigate(value);
    setYear(value);
  }

  function getYearOptions() {
    const yearOptions = [];
    const initial = (getYear(date) - 100);
    const n = (getYear(date) + 100);
    for (let i = initial; i < n; i++) {
      yearOptions.push({value: i.toString(), label: i.toString()});
    }
    return yearOptions;
  }

  return (
    <Grid container style={{paddingLeft: '8px', marginBottom: '22px'}} justifyContent='space-between' alignItems='center'>
      <Grid className={classes.header} item>
        <div style={{marginRight: '20px'}}>
          <Dropdown options={translate('common:editors.dateTime.months', {returnObjects: true})}
            value={month}
            autoComplete={false}
            onChange={changeMonth} />
        </div>
        <div>
          <Dropdown options={getYearOptions()}
            value={year.toString()}
            autoComplete={false}
            onChange={changeYear} />
        </div>
      </Grid>
      <div style={{display: 'flex'}}>
        <Grid item>
          <IconButton className={classes.icon}
            size='small'
            aria-label='Previous month'
            onClick={onPrevious}>
            <ChevronLeft color='action' />
          </IconButton>
        </Grid>
        <Grid item>
          <IconButton className={classes.icon}
            size='small'
            aria-label='Next month'
            onClick={onNext}>
            <ChevronRight color='action' />
          </IconButton>
        </Grid>
      </div>
    </Grid>
  );
};

export default Header;
